import React, { useState, useEffect, useRef } from 'react';
import {
    Modal,
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    Stack,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Paper,
    Divider
} from '@mui/material';
import { CloseIcon, DeleteIcon, AttachFileIcon } from 'icons/materialIcons';
import { useAuth } from 'contexts/AuthContext'; // Adjust path as needed
import { sendSupportEmail } from 'apis/emailApi';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';

const SendEmailModal = ({ open, onClose }) => {
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const fileInputRef = useRef(null);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const { authState } = useAuth();

    useEffect(() => {
        if (open) {
            setSubject('');
            setMessage('');
            setAttachments([]);
        }
    }, [open]);

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleFileChange = (event) => {
        const newFiles = Array.from(event.target.files);

        // Check file size limit (10MB per file)
        const oversizedFiles = newFiles.filter(file => file.size > 10 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            setSnackbar({
                open: true,
                message: 'Some files exceed the 10MB size limit and were not added',
                severity: 'warning'
            });
        }

        // Filter out oversized files and add to attachments
        const validFiles = newFiles.filter(file => file.size <= 10 * 1024 * 1024);

        // Check total number of attachments (limit to 5)
        if (attachments.length + validFiles.length > 5) {
            setSnackbar({
                open: true,
                message: 'Maximum 5 attachments allowed',
                severity: 'warning'
            });
            // Only add files up to the limit
            setAttachments(prev => [
                ...prev,
                ...validFiles.slice(0, 5 - prev.length)
            ]);
        } else {
            setAttachments(prev => [...prev, ...validFiles]);
        }

        // Reset the file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const handleSendEmail = async () => {
        if (!subject.trim() || !message.trim()) return;

        setIsSubmitting(true);
        try {
            // Create FormData to handle file uploads
            const formData = new FormData();
            formData.append('subject', subject.trim());
            formData.append('message', message.trim());

            // Add each attachment to the FormData
            attachments.forEach((file) => {
                formData.append('attachments', file);
            });

            const response = await sendSupportEmail(
                formData,
                authState.token
            );

            if (response.success) {
                setSnackbar({
                    open: true,
                    message: 'Email sent successfully!',
                    severity: 'success'
                });
                onClose();
            } else {
                setSnackbar({
                    open: true,
                    message: response.message,
                    severity: 'error'
                });
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Failed to send email. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const isSendDisabled = subject.trim().length === 0 || message.trim().length === 0 || isSubmitting;

    const buttonStyles = {
        padding: '8px 45px',
        textTransform: 'none',
        borderRadius: '26px',
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        else return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
                aria-labelledby="send-email-modal"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(8px)',
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: { xs: '90%', sm: '75%', md: '70%', lg: '60%' },
                        maxWidth: 700,
                        minHeight: 600,
                        bgcolor: '#ffffff',
                        borderRadius: 3,
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <IconButton
                        onClick={onClose}
                        sx={{ position: 'absolute', top: 16, right: 16 }}
                        disabled={isSubmitting}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Typography id="send-email-modal-title" variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1.5rem', mt: 3 }}>
                        Email hello@intouchwe.com
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 3, fontSize: '0.9rem', color: '#828282' }}>
                        Let us know how we can assist you. We usually respond to emails within 3 hours.
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Subject *
                    </Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        disabled={isSubmitting}
                        sx={{
                            mb: 2,
                            bgcolor: '#f7f7f7',
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }}
                    />

                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Message *
                    </Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Message"
                        multiline
                        rows={6}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        disabled={isSubmitting}
                        sx={{
                            mb: 2,
                            bgcolor: '#f7f7f7',
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }}
                    />

                    {/* Attachments Section */}
                    <Box sx={{ mb: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="body2">
                                Attachments {attachments.length > 0 && `(${attachments.length}/5)`}
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<AttachFileIcon />}
                                onClick={() => fileInputRef.current.click()}
                                disabled={isSubmitting || attachments.length >= 5}
                                sx={{
                                    textTransform: 'none',
                                    borderColor: '#e0e0e0',
                                    color: '#212121',
                                    '&:hover': {
                                        borderColor: '#bdbdbd',
                                    },
                                    '&.Mui-disabled': {
                                        color: '#bdbdbd',
                                    }
                                }}
                            >
                                Add File
                            </Button>
                            <input
                                type="file"
                                multiple
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                                ref={fileInputRef}
                                disabled={isSubmitting}
                            />
                        </Stack>

                        {attachments.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f7f7f7', borderColor: '#e0e0e0' }}>
                                <List dense disablePadding>
                                    {attachments.map((file, index) => (
                                        <React.Fragment key={index}>
                                            {index > 0 && <Divider component="li" />}
                                            <ListItem>
                                                <ListItemText
                                                    primary={file.name}
                                                    secondary={formatFileSize(file.size)}
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                    secondaryTypographyProps={{ variant: 'caption' }}
                                                />
                                                <ListItemSecondaryAction>
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleRemoveAttachment(index)}
                                                        disabled={isSubmitting}
                                                        size="small"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Paper>
                        )}

                        <Typography variant="caption" sx={{ color: '#828282', display: 'block', mt: 1 }}>
                            Max 5 files, 10MB per file. Supported formats: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX.
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            disabled={isSubmitting}
                            sx={{
                                ...buttonStyles,
                                bgcolor: 'transparent',
                                color: '#212121',
                                fontWeight: '500',
                                border: '1px solid #c4c4c4',
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleSendEmail}
                            disabled={isSendDisabled}
                            sx={{
                                ...buttonStyles,
                                bgcolor: isSendDisabled ? '#e0e0e0' : '#000000',
                                color: isSendDisabled ? '#a6a6a6' : '#ffffff',
                            }}
                        >
                            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Send'}
                        </Button>
                    </Stack>
                </Box>
            </Modal>

            <NotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={handleSnackbarClose}
            />
        </>
    );
};

export default SendEmailModal;